import React from 'react';

// services
import {CreditBookApi} from '../../shared/services/ApiService';

// helpers
import {useQuery} from '@tanstack/react-query';
import {useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {useTheme} from '@emotion/react';

// components
import {
  CardEmptyState,
  CardError,
  CardDetailsLoan,
  PageTemplate,
  CardDetailsSkeleton,
  CardTableSkeleton,
} from '../../shared/components';
import {IconNoPayments} from './LoanDetailsPage-styles';
import {SectionPayments} from './sections';

// types
import {
  CreditBookApiError,
  LoanDetails,
  LoanPayment,
} from '../../shared/services/ApiService/types';

/**
 * Fetch the loan details
 * @param loanId
 */
const fetchLoanDetails = async (loanId: string | undefined) => {
  const response = await CreditBookApi.loan.getLoanDetails(Number(loanId));
  return response.data;
};

/**
 * Fetch the loan payments
 * @param loanId
 */
const fetchLoanPayments = async (loanId: string | undefined) => {
  const response = await CreditBookApi.loan.getLoanPayment(Number(loanId));
  return response.data;
};

const LoanDetailsPage = () => {
  const {t} = useTranslation();
  const {loanId} = useParams();
  const {skeleton} = useTheme();

  // Fetch the loan details
  const {
    data: loanDetails,
    isLoading: loanDetailsLoading,
    error: loanDetailsError,
    isRefetching: isLoanDetailsRefetching,
  } = useQuery<LoanDetails, CreditBookApiError>({
    queryKey: ['getLoanDetails'],
    queryFn: () => fetchLoanDetails(loanId),
  });

  // Fetch the loan payments
  const {
    data: payments,
    isLoading: paymentsLoading,
    error: paymentsError,
    isRefetching: isPaymentsRefetching,
  } = useQuery<LoanPayment[], CreditBookApiError>({
    queryKey: ['getLoanPayments'],
    queryFn: () => fetchLoanPayments(loanId),
    initialData: [],
  });

  /**
   * Render the loan details
   */
  const renderLoanDetails = () => {
    if (loanDetailsLoading || isLoanDetailsRefetching) {
      return <CardDetailsSkeleton variant="detailed" />;
    }

    if (loanDetailsError || !loanDetails) {
      return (
        <CardError message={loanDetailsError?.response?.data?.errorMessage} />
      );
    }

    return <CardDetailsLoan variant="detailed" loan={loanDetails} />;
  };

  /**
   * Render the payments
   */
  const renderPayments = () => {
    if (paymentsLoading || isPaymentsRefetching) {
      return (
        <CardTableSkeleton
          columns={[
            {width: skeleton.text.width.sm, empty: false},
            {width: skeleton.text.width.lg, empty: false},
            {width: skeleton.text.width.md, empty: false},
            {width: skeleton.text.width.md, empty: false},
            {width: skeleton.text.width.md, empty: false},
            {empty: true},
          ]}
          rowCount={3}
        />
      );
    }

    if (paymentsError) {
      return (
        <CardError
          title={t('loan_payments')}
          message={paymentsError?.response?.data?.errorMessage}
        />
      );
    }

    if (payments.length === 0) {
      return (
        <CardEmptyState
          title={t('loan_payments')}
          text={t('loan_paymentEmptyState')}
          icon={IconNoPayments}
        />
      );
    }

    return <SectionPayments payments={payments} isVariableCredit={loanDetails?.isVariableCredit} />;
  };

  return (
    <PageTemplate
      title={
        loanDetails?.loanNumber
          ? `${t('loan_detailsLoan')} #${loanDetails?.loanNumber}`
          : t('loan_detailsLoan')
      }>
      {renderLoanDetails()}
      {renderPayments()}
    </PageTemplate>
  );
};

export default LoanDetailsPage;
